import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const AuthPage = ({ onAuth }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Retry logic for transient failures
    const maxRetries = 2;
    let lastError = null;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await axios.post(`${API}/auth/login`, loginData, {
          timeout: 15000 // 15 second timeout
        });
        const user = response.data;
        onAuth(user);
        
        // Redirect admins to admin portal, regular users to dashboard
        if (user.is_admin || user.role === 'admin' || user.role === 'super_admin') {
          toast.success('Welcome back, Admin!');
          navigate('/admin');
        } else {
          toast.success('Welcome back!');
          navigate('/dashboard');
        }
        return; // Success
        
      } catch (error) {
        lastError = error;
        
        // Only retry on network/timeout errors
        const isRetryable = error.code === 'ERR_NETWORK' || 
                            error.code === 'ECONNABORTED' ||
                            error.response?.status === 502 || 
                            error.response?.status === 503;
        
        if (isRetryable && attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
          continue;
        }
        break;
      }
    }
    
    // Handle error with user-friendly message
    let errorMessage = 'Login failed. Please try again.';
    if (lastError?.code === 'ERR_NETWORK') {
      errorMessage = 'Unable to connect. Please check your internet connection.';
    } else if (lastError?.response?.status === 502 || lastError?.response?.status === 503) {
      errorMessage = 'Our servers are temporarily busy. Please try again.';
    } else if (lastError?.response?.data?.detail) {
      errorMessage = lastError.response.data.detail;
    }
    
    toast.error(errorMessage);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="text-[#881337]" size={32} fill="#881337" />
            <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-[#57534E]">Welcome back! Log in to continue planning.</p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8">
          <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-6 text-center">Log In</h2>

          <form data-testid="login-form" onSubmit={handleLogin} className="space-y-6">
            <div>
              <Label htmlFor="login-email" className="text-[#1C1917] mb-2 block">Email</Label>
              <Input
                data-testid="login-email-input"
                id="login-email"
                type="email"
                value={loginData.email}
                onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0"
                required
              />
            </div>
            <div>
              <Label htmlFor="login-password" className="text-[#1C1917] mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  data-testid="login-password-input"
                  id="login-password"
                  type={showPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917] transition-colors"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            <Button 
              data-testid="login-submit-btn"
              type="submit" 
              disabled={loading}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white transition-all duration-300"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-[#57534E] text-sm">
              Don't have an account?{' '}
              <button
                onClick={() => navigate('/signup')}
                className="text-[#881337] font-medium hover:underline"
              >
                Sign up
              </button>
            </p>
            <p className="text-[#57534E] text-sm">
              <Link
                to="/forgot-password"
                className="text-[#881337] font-medium hover:underline"
              >
                Forgot your password?
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;